import React, { useEffect, useState } from "react";
import { CiMenuFries } from "react-icons/ci";
import { IoClose } from "react-icons/io5";
import { NAVBAR_ITEMS } from ".";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChangeLngButtonMain } from "../../ChangeLng/ChangeLngButtonMain";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../../assets/images/logo.png";
import { useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";

export const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(window.scrollY > 20);
  const { t } = useTranslation();
  const location = useLocation();

  const { data: userData } = useAppSelector((state: RootState) => state.userMeta);
  const isLoggedIn = !!userData;

  // Hide the "Order" link for logged-in users — they go through /payment/checkout instead
  const visibleItems = NAVBAR_ITEMS.filter(item =>
    isLoggedIn ? item.path !== '/place-order' : true
  );

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) closeMenu();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);


  return (
    <nav
      className={`fixed top-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-7xl z-50 transition-all duration-300 rounded-full border border-white/5 ${isScrolled
        ? "bg-[#030014]/70 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.1)] py-3"
        : "bg-transparent py-4"
        }`}
    >
      <div className="w-full px-6 lg:px-8 flex items-center justify-between">

        {/* LOGO */}
        <Link to="/" className="flex items-center gap-2 group z-[60]" onClick={closeMenu}>
          <img
            src={logo}
            alt="VIART Logo"
            className="w-8 h-8 lg:w-11 lg:h-11 object-contain transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        {/* DESKTOP NAV */}
        <div className="hidden lg:flex items-center space-x-10">
          <ul className="flex items-center space-x-8">
            {visibleItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    className={`relative text-sm font-medium tracking-wide transition-colors duration-300 ${isActive ? "text-white" : "text-gray-400 hover:text-white"
                      }`}
                  >
                    {t("navbar." + item.name)}
                    {isActive && (
                      <motion.div
                        layoutId="navbar-indicator"
                        className="absolute -bottom-1.5 left-0 right-0 h-[2px] rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 shadow-[0_0_8px_rgba(168,85,247,0.5)]"
                        initial={false}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="w-[1px] h-6 bg-white/10" />

          {/* LANGUAGE PICKER (DESKTOP) */}
          <ChangeLngButtonMain initialLanguage="en" closeAction={closeMenu} />
        </div>

        {/* MOBILE TOGGLE BUTTON */}
        <button
          className="lg:hidden z-[60] relative p-1.5 text-gray-300 hover:text-white focus:outline-none transition-colors rounded-lg bg-white/5 hover:bg-white/10 border border-white/10"
          onClick={toggleMenu}
          aria-label="Toggle Menu"
        >
          {isMenuOpen ? (
            <IoClose className="w-5 h-5" />
          ) : (
            <CiMenuFries className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* MOBILE NAV DROPDOWN REGION */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95, transition: { duration: 0.3, delay: 0.2, ease: "easeIn" } }}
            className="absolute top-[calc(100%+0.5rem)] left-0 w-full max-h-[calc(100dvh-6rem)] overflow-y-auto bg-[#030014]/95 backdrop-blur-3xl lg:hidden flex flex-col py-8 px-6 rounded-3xl border border-white/10 shadow-2xl z-40"
          >
            <ul className="flex flex-col space-y-6 items-center">
              {visibleItems.map((item, idx) => {
                const isActive = location.pathname === item.path;
                return (
                  <motion.li
                    key={item.id}
                    initial={{ opacity: 0, y: 30, scale: 0.9, filter: "blur(8px)" }}
                    animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                    exit={{
                      opacity: 0,
                      y: -20,
                      scale: 0.95,
                      filter: "blur(4px)",
                      transition: { delay: (NAVBAR_ITEMS.length - idx - 1) * 0.04, duration: 0.2, ease: [0.22, 1, 0.36, 1] }
                    }}
                    transition={{ delay: idx * 0.06, type: "spring", stiffness: 300, damping: 24 }}
                  >
                    <Link
                      to={item.path}
                      onClick={closeMenu}
                      className={`text-lg sm:text-2xl tracking-widest font-light transition-all flex flex-col items-center ${isActive ? "text-white font-medium" : "text-gray-400 hover:text-purple-400"
                        }`}
                    >
                      {t("navbar." + item.name)}
                      {isActive && (
                        <div className="h-[2px] w-full rounded-full mt-1.5 bg-gradient-to-r from-purple-500 to-indigo-500 opacity-50" />
                      )}
                    </Link>
                  </motion.li>
                );
              })}

              {/* Mobile Language Button */}
              <motion.li
                initial={{ opacity: 0, y: 30, scale: 0.9, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                exit={{
                  opacity: 0,
                  y: -20,
                  scale: 0.95,
                  filter: "blur(4px)",
                  transition: { delay: NAVBAR_ITEMS.length * 0.04, duration: 0.2, ease: [0.22, 1, 0.36, 1] }
                }}
                transition={{ delay: NAVBAR_ITEMS.length * 0.06, type: "spring", stiffness: 300, damping: 24 }}
                className="pt-6 w-full flex justify-center border-t border-white/5 mt-4"
              >
                <div className="flex flex-col items-center">
                  <span className="text-white/30 text-xs mb-3 uppercase tracking-widest">
                    {t("globals.language")}
                  </span>
                  <ChangeLngButtonMain initialLanguage="en" closeAction={closeMenu} containerClassName="w-auto" dropdownPosition="top" />
                </div>
              </motion.li>
            </ul>

            {/* Ambient background decoration in mobile menu */}
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-purple-600/10 to-transparent -z-10 rounded-b-3xl" />
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
